import LZString from 'lz-string';

/**
 * URL Filter Utilities
 *
 * Provides functions to encode/decode filter state for shareable URLs
 * Uses LZ-String compression to keep URLs manageable
 */

/**
 * Encode filters to URL-safe compressed string
 *
 * @param filters - Filter object to encode
 * @returns Compressed, URL-safe string
 */
export function encodeFiltersToUrl<T extends Record<string, any>>(filters: T): string {
  try {
    const json = JSON.stringify(filters, (key, value) => {
      // Skip circular references and functions
      if (typeof value === 'object' && value !== null) {
        if (
          value.constructor?.name === 'WeakMap' ||
          value.constructor?.name === 'WeakSet'
        ) {
          return undefined;
        }
      }
      return value;
    });
    const compressed = LZString.compressToEncodedURIComponent(json);
    return compressed;
  } catch (error) {
    console.error('Failed to encode filters:', error);
    throw new Error('Failed to encode filters for URL');
  }
}

/**
 * Decode filters from URL-safe compressed string
 *
 * @param encoded - Compressed string from URL
 * @returns Decoded filter object or null if invalid
 */
export function decodeFiltersFromUrl<T extends Record<string, any>>(
  encoded: string,
): T | null {
  try {
    const decompressed = LZString.decompressFromEncodedURIComponent(encoded);
    if (!decompressed) {
      console.error('Failed to decompress filters');
      return null;
    }
    const parsed = JSON.parse(decompressed);
    return parsed as T;
  } catch (error) {
    console.error('Failed to decode filters:', error);
    return null;
  }
}

/**
 * Generate shareable URL with encoded filters
 *
 * @param basePath - Base path (e.g., '/audits')
 * @param filters - Filter object to encode
 * @param businessUnitId - Business unit ID for validation
 * @returns Complete URL with encoded filters
 */
export function generateShareableUrl<T extends Record<string, any>>(
  basePath: string,
  filters: T,
  businessUnitId: string,
): string {
  const encoded = encodeFiltersToUrl(filters);
  const params = new URLSearchParams({
    filters: encoded,
    bu: businessUnitId,
  });
  return `${basePath}?${params.toString()}`;
}

/**
 * Parse shared filters from URL search params
 *
 * @param searchParams - URLSearchParams from location.search
 * @returns Object with decoded filters and business unit ID, or null if not a shared URL
 */
export function parseSharedFiltersFromUrl<T extends Record<string, any>>(
  searchParams: URLSearchParams,
): { filters: T; businessUnitId: string } | null {
  const encodedFilters = searchParams.get('filters');
  const businessUnitId = searchParams.get('bu');

  if (!encodedFilters || !businessUnitId) {
    return null;
  }

  const filters = decodeFiltersFromUrl<T>(encodedFilters);
  if (!filters) {
    return null;
  }

  return { filters, businessUnitId };
}
